import { useState } from 'react';
import { CheckIcon, XMarkIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useSuggestions, useReviewSuggestion, useMarkApplied } from '../hooks/useDirectory';

// Review queue for directory corrections. Full-admin only — the API returns 403
// otherwise and the panel simply doesn't render.
//
// This queue is the ONLY tracker of outstanding corrections: approving emails
// nobody, because the corrections are taken to HR / Operations / IT by hand. So
// "approved" is not the end of the story, and the UI has to make the difference
// between approved and applied obvious, or things quietly rot here.

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-900 ring-amber-600/30',
  approved: 'bg-sky-100 text-sky-800 ring-sky-600/30',
  applied: 'bg-emerald-100 text-emerald-800 ring-emerald-600/30',
  rejected: 'bg-gray-100 text-gray-600 ring-gray-500/30',
};

const STATUS_LABELS = {
  pending: 'Needs your decision',
  approved: 'Approved — not yet applied',
  applied: 'Applied',
  rejected: 'Rejected',
};

function when(value) {
  if (!value) return '';
  return new Date(value).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function SuggestionRow({ s, onReview, onApplied, busy }) {
  return (
    <div className="border-t border-gray-100 px-4 py-3 first:border-t-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-gray-900">{s.subject_display_name}</span>
            <span className="text-sm text-gray-500">· {s.field_label}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${STATUS_STYLES[s.status]}`}>
              {STATUS_LABELS[s.status] || s.status}
            </span>
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-rose-700 line-through">{s.current_value || '(empty)'}</span>
            <ArrowRightIcon className="h-3.5 w-3.5 text-gray-400" />
            <span className="font-medium text-emerald-700">{s.suggested_value || '(clear it)'}</span>
          </div>

          {s.reason ? <p className="mt-1 text-xs text-gray-500">“{s.reason}”</p> : null}

          <p className="mt-1 text-xs text-gray-400">
            Raised by {s.suggested_by_name || 'unknown'} · {when(s.suggested_at)}
            {s.reviewed_by_name ? ` · reviewed by ${s.reviewed_by_name}` : ''}
            {s.applied_at ? ` · applied ${when(s.applied_at)}` : ''}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {/* Who owns the field, so the next step needs no lookup. */}
          <span className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-600">
            Fix in <span className="font-medium text-gray-900">{s.owner_system}</span>
          </span>

          {s.status === 'pending' ? (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() => onReview(s.id, 'approved')}
                className="flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                <CheckIcon className="h-4 w-4" /> Approve
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => onReview(s.id, 'rejected')}
                className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <XMarkIcon className="h-4 w-4" /> Reject
              </button>
            </>
          ) : null}

          {s.status === 'approved' ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => onApplied(s.id)}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Mark as applied
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function SuggestionsQueue() {
  const { data, isLoading, error } = useSuggestions();
  const review = useReviewSuggestion();
  const markApplied = useMarkApplied();
  const [open, setOpen] = useState(false);

  // A 403 means "not an admin" — not an error worth showing anyone.
  if (error || isLoading) return null;

  const suggestions = data?.suggestions || [];
  const pending = data?.pending || 0;
  // Approved but not yet done. Deliberately counted separately: it's the number
  // that grows silently if nobody chases the owning team.
  const awaiting = suggestions.filter((s) => s.status === 'approved').length;

  if (!suggestions.length) return null;

  const busy = review.isPending || markApplied.isPending;

  return (
    <div className="mb-4 rounded-lg border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-gray-900">Suggested corrections</span>
          {pending ? (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900 ring-1 ring-inset ring-amber-600/30">
              {pending} need{pending === 1 ? 's' : ''} your decision
            </span>
          ) : null}
          {awaiting ? (
            <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800 ring-1 ring-inset ring-sky-600/30">
              {awaiting} approved, waiting on the owning system
            </span>
          ) : null}
          {!pending && !awaiting ? <span className="text-xs text-gray-400">nothing outstanding</span> : null}
        </span>
        <span className="text-xs text-gray-400">{open ? 'Hide' : 'Show'}</span>
      </button>

      {open ? <div className="border-t border-gray-200">
        {suggestions.map((s) => (
          <SuggestionRow
            key={s.id}
            s={s}
            busy={busy}
            onReview={(id, decision) => review.mutate({ id, decision })}
            onApplied={(id) => markApplied.mutate(id)}
          />
        ))}
      </div> : null}
    </div>
  );
}

export default SuggestionsQueue;
